import {jsx} from 'panel';
import type {JsxVNode, JsxVNodeChildren, JsxVNodeProps} from 'snabbdom-jsx-lite';

/**
 * snabbdom only allows string | boolean | number | null | undefined in attrs
 * since we allow pojos (plain old javascript objects and arrays) in attrs, define a broader attrs type
 */
export interface PojoAttrsJsxProps {
  attrs?: Record<string, any>;
  sel?: string;
  on?: Record<string, Listener<any> | Array<Listener<any>>>;
  hook?: JsxVNodeProps[`hook`];
  style?: JsxVNodeProps[`style`];
  key?: JsxVNodeProps[`key`];
  class?: JsxVNodeProps[`class`];
  props?: Record<string, any>;
  params?: Record<string, any>;
}

/**
 * JSON.stringify objects and arrays in attrs since dom attributes can only have simple string values
 */
export function stringifyAttrsIfPojo(attrs: Record<string, any>): Record<string, any> {
  // see https://jsperf.com/object-keys-vs-for-in-with-closure/3
  // Object.keys is faster than for-in -> .hasOwnProperty
  const attrKeys = Object.keys(attrs);
  for (let i = 0, len = attrKeys.length; i < len; ++i) {
    const attr = attrKeys[i];
    const attrValue = attrs[attr];

    // typeof null = 'object' is a common pitfall, so we gotta check for that
    if (attrValue !== null && typeof attrValue === `object`) {
      // only stringify plain objects or arrays
      const constructor = attrValue.constructor;
      if (constructor === Object || constructor === Array) {
        attrs[attr] = JSON.stringify(attrValue);
      } else {
        throw new Error(
          `cannot JSON.stringify attr:'${attr}' with value:${attrValue}, only plain objects and arrays allowed`,
        );
      }
    }
  }

  return attrs;
}

export type StrictJsxProps<PropsT extends PojoAttrsJsxProps> = {
  attrs?: PropsT[`attrs`] & {
    elref?: string;
    tabindex?: number;
    'style-override'?: string;
    style?: string;
    slot?: string;
    id?: string | number;
    title?: string;
    role?: string;
    color?: string;
    'aria-label'?: string;
    required?: boolean;
    autocomplete?: string;
    type?: string;
    autoFocus?: boolean;
  };
  props?: PropsT[`props`];
  sel?: string;
  hook?: JsxVNodeProps[`hook`];
  style?: JsxVNodeProps[`style`];
  key?: JsxVNodeProps[`key`];
  class?: JsxVNodeProps[`class`];
  on?: PropsT[`on`] & Omit<JsxVNodeProps[`on`], keyof PropsT[`on`]>;
  params?: PropsT[`params`];
};

export type JsxFactory<PropsT extends PojoAttrsJsxProps> = ((
  props: StrictJsxProps<PropsT>,
  children?: JsxVNodeChildren,
) => JsxVNode) & {
  _tagName: string;
};

/**
 * defines a custom element via customElement.define
 * and returns a jsx factory function that auto JSON.stringify-ies POJO attrs
 *
 * @param customElementName - tag name
 * @param customElementClass - element class
 * @param options - options
 */
export function defineAndMakeJsxFactory<PropsT extends PojoAttrsJsxProps>(
  customElementName: string,
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  customElementClass: Function,
): JsxFactory<PropsT> {
  // register with customElements registry if not already defined
  if (!customElements.get(customElementName)) {
    customElements.define(customElementName, customElementClass);
  }

  const renderer = function (jsxProps?: StrictJsxProps<PropsT>, children?: JsxVNodeChildren) {
    if (!jsxProps) {
      jsxProps = {} as StrictJsxProps<PropsT>;
    }
    if (jsxProps.attrs) {
      jsxProps.attrs = stringifyAttrsIfPojo(jsxProps.attrs);
    }
    if (!jsxProps.props) {
      jsxProps.props = {};
    }
    return jsx(customElementName, jsxProps, children);
  };

  renderer._tagName = customElementName;

  return renderer;
}
